import React, { useState, useEffect, useContext } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  add,
  isToday,
  isSameMonth,
  startOfDay,
} from 'date-fns';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  MapPinIcon,
  EllipsisHorizontalIcon,
  DocumentIcon,
  FlagIcon,
  XCircleIcon,
  UserCircleIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/20/solid';
import StaffNav from './managernav';
import { useNavigate } from 'react-router-dom';
import LoadingComponent from './loading';
import { TruckIcon } from '@heroicons/react/24/outline';
import Travel from './travel';
import { AuthContext } from '../AuthContext';
import { connectGoogleCalendar } from '../utils/googleSync';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Meetings() {
  const Navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [meetings, setMeetings] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [travelComponent, setTravelComponent] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState([]);
  const [activeTab, setActiveTab] = useState('meetings'); // 'meetings' or 'suggestions'



  const user = useContext(AuthContext);
  const isStaffUser = String(user?.access || '').toLowerCase() === 'staff';

  // Use currentMonth for calendar view
  const startDate = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
  const endDate = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });

  const days = [];
  let day = startOfDay(startDate);
  while (day <= endDate) {
    days.push(day);
    day = add(day, { days: 1 });
  }



  const fetchmeetings = async (date) => {
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const response = await fetch(`https://api.insightos.co.uk/api/meetings/${formattedDate}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const normalizeAttendees = (value) =>
        String(value || '')
          .split(',')
          .map((entry) => entry.trim().toLowerCase())
          .filter(Boolean);

      const matchesCurrentStaffUser = (item) => {
        if (String(user?.access || '').toLowerCase() !== 'staff') {
          return true;
        }

        const currentUserName = String(user?.name || '').trim().toLowerCase();
        if (!currentUserName) {
          return false;
        }

        return normalizeAttendees(item?.Attendees || item?.MeetingAttendees).includes(currentUserName);
      };

      const meetingRows = Array.isArray(data.data) ? data.data.filter(matchesCurrentStaffUser) : [];
      const suggestionRows = Array.isArray(data.aiSuggestions)
        ? data.aiSuggestions.filter(matchesCurrentStaffUser)
        : [];

      setMeetings(meetingRows);
      setAISuggestions(suggestionRows);
    } catch (error) {
      console.error(error);
    }
  };

  const previousMonth = () => setCurrentMonth(add(currentMonth, { months: -1 }));
  const nextMonth = () => setCurrentMonth(add(currentMonth, { months: 1 }));

  // When currentMonth changes, update selectedDate and fetch meetings for the first day of the new month
  useEffect(() => {
    setSelectedDate(startOfMonth(currentMonth));
    fetchmeetings(startOfMonth(currentMonth));
    // eslint-disable-next-line
  }, [currentMonth]);

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/deletemeeting/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      await response.json();
      fetchmeetings(selectedDate);
      setUpdated("Meeting Deleted");
      window.location.reload()
    }
    catch (error) {
      console.error(error);
    }
  };

  const countWordsAndCost = (str) => {
    if (!str || typeof str !== 'string') {
      return { wordCount: 0, credit: 0 };
    }

    const wordCount = str.split(/\s+/).filter(Boolean).length;
    const tokens = wordCount * 0.75;
    const costPerToken = 0.003 / 1000;
    const aiCost = tokens * costPerToken;
    const credit = aiCost < 0.1 ? 1 : aiCost;
    return { wordCount, credit };
  };

  const handleGenerateAI = async (meetingId) => {
    setLoading(true);
    setAiSummary(null);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/meetingai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ meetingId }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setAiSummary(data.summary || "AI summary generated.");
      setUpdated("AI Summary Generated");
      fetchmeetings(selectedDate);
    } catch (error) {
      setAiSummary("Failed to generate AI summary.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toSeconds = (time = '00:00:00') => {
    const [hour = '0', minute = '0', second = '0'] = time.split(':');
    const safeParse = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return safeParse(hour) * 3600 + safeParse(minute) * 60 + safeParse(second);
  };

  const sortedMeetings = [...meetings].sort((a, b) => toSeconds(a.Time) - toSeconds(b.Time));

  const sortedAISuggestions = [...aiSuggestions].sort((a, b) => toSeconds(a.TimeFrom) - toSeconds(b.TimeFrom));

  const handleApproveAISuggestion = async (suggestionId) => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/aisuggestion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ suggestionId }),
      });

      if (!response.ok) {
        throw new Error('Failed to approve suggestion');
      }

      setUpdated("AI Suggestion Approved!");
      fetchmeetings(selectedDate);
    } catch (error) {
      console.error('Error approving suggestion:', error);
      setUpdated("Failed to approve suggestion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-28 md:pb-16">
      {loading && <LoadingComponent />}

      {report && (
        <div className="rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl p-4 text-center text-indigo-900 shadow-[0_12px_30px_rgba(99,102,241,0.2)]">
          <button onClick={() => setReport(null)} className="text-red-700 hover:underline">
            <span className="sr-only">Close</span>
            <XCircleIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <h3 className="font-semibold">Meeting Report</h3>
          <div className="mt-2 rounded-xl border border-indigo-200/60 bg-white/70 p-4 text-center text-indigo-900">
            <p className="text-sm"><strong>Name:</strong> {report.Name}</p>
            <p className="text-sm"><strong>Attendees:</strong> {report.Attendees}</p>
            <p className="text-sm"><strong>Date:</strong> {new Date(report.Date).toLocaleDateString()}</p>
            <p className="text-sm"><strong>Time:</strong> {report.Time}</p>
            <p className="text-sm"><strong>Location:</strong> {report.Location}</p>
            <p className="text-sm">
              <strong>AI Used:</strong> {report?.AIUsed ? report.AIUsed : "No"}
            </p>
            <p className="text-sm text-blue-700">
              <strong>Word Count:</strong> {countWordsAndCost(report.MeetingNotes).wordCount}
            </p>
            <p className="text-sm text-blue-700">
              <strong>Cost Credit:</strong> {countWordsAndCost(report.MeetingNotes).credit}
            </p>
            <button
              onClick={() => handleGenerateAI(report.id)}
              className="inline-flex mt-1 items-center gap-x-1.5 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-2.5 py-1.5 text-sm font-semibold text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95"
            >
              Generate AI Summary
            </button>
          </div>
          <div className="mt-4 rounded-xl border border-rose-200/60 bg-rose-50/70 p-2">
            {aiSummary && (
              <div className="mt-2">
                <span className="block text-gray-600 font-medium">AI Generated:</span>
                <p className="text-blue-700">{aiSummary}</p>
                <button
                  onClick={() => setAiSummary(null)}
                  type="button"
                  className="rounded-xl mt-2 ml-3 border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 px-3.5 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-900/35 hover:from-rose-500/95 hover:to-red-400/95"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="mt-2">
            <span className="block text-gray-600 font-medium">Meeting Notes:</span>
            <p className="text-blue-700">{report.MeetingNotes}</p>
          </div>
        </div>
      )}


      {!report && !updated && (
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-10 xl:gap-x-12 pb-24 md:pb-12">
          {/* Calendar Section */}
          <div className="mt-10 text-center lg:col-start-7 lg:col-end-13 lg:row-start-1 lg:mt-8 xl:col-start-7">
            <div className="rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3 sm:p-4 md:p-5 lg:p-6">
            <div className="flex items-center gap-2 text-indigo-900">
              <button
                type="button"
                onClick={previousMonth}
                className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 shadow-sm transition-all hover:bg-white md:h-10 md:w-10"
              >
                <span className="sr-only">Previous month</span>
                <ChevronLeftIcon className="size-5" aria-hidden="true" />
              </button>
              <div className="flex-auto rounded-xl border border-indigo-200/70 bg-white/70 px-3 py-2 text-sm font-semibold text-indigo-900 md:px-4 md:py-2.5 md:text-base">{format(currentMonth, 'MMMM yyyy')}</div>
              <button
                type="button"
                onClick={nextMonth}
                className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 shadow-sm transition-all hover:bg-white md:h-10 md:w-10"
              >
                <span className="sr-only">Next month</span>
                <ChevronRightIcon className="size-5" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-7 gap-1 rounded-xl border border-indigo-200/60 bg-white/60 p-2 text-xs/6 text-indigo-700 md:gap-1.5 md:p-2.5 md:text-sm">
              <div>M</div>
              <div>T</div>
              <div>W</div>
              <div>T</div>
              <div>F</div>
              <div>S</div>
              <div>S</div>
            </div>

            <div className="isolate mt-2 grid grid-cols-7 gap-1 rounded-xl border border-indigo-200/60 bg-white/65 p-2 text-sm shadow-[0_12px_24px_rgba(99,102,241,0.14)] md:gap-1.5 md:p-2.5 md:text-base">
              {days.map((day, dayIdx) => (
                <button
                  key={day.toISOString()}
                  onClick={() => {
                    setSelectedDate(day);
                    fetchmeetings(day);
                  }}
                  type="button"
                  className={classNames(
                    'py-1.5 transition-all focus:z-10 rounded-lg border',
                    isSameMonth(day, currentMonth) ? 'bg-white/85 border-indigo-100/80 text-indigo-900 hover:bg-white' : 'bg-white/45 border-transparent text-indigo-400 hover:bg-white/60',
                    isToday(day) && 'ring-2 ring-sky-400 text-sky-700 font-bold',
                    selectedDate && day.toDateString() === selectedDate.toDateString() && 'bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border-white/20 text-white shadow-lg shadow-indigo-950/35',
                    dayIdx === 0 && 'rounded-tl-lg',
                    dayIdx === 6 && 'rounded-tr-lg',
                    dayIdx === days.length - 7 && 'rounded-bl-lg',
                    dayIdx === days.length - 1 && 'rounded-br-lg'
                  )}
                >
                  <time dateTime={day.toISOString()} className="mx-auto flex size-7 items-center justify-center rounded-full md:size-9 lg:size-10">
                    {format(day, 'd')}
                  </time>
                </button>
              ))}
            </div>
            </div>
          </div>

          {/* meetings Section */}
          <ol className="mt-4 text-sm lg:col-span-6 xl:col-span-6 pb-20 md:pb-8">
            <h2 className="text-base font-semibold text-gray-900">Meeting Schedule</h2>
            
            {/* Tab Buttons */}
            <div className="mt-3 mb-3 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-2">
              <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setActiveTab('meetings')}
                className={`flex-1 min-w-[120px] inline-flex items-center justify-center gap-x-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold shadow-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  activeTab === 'meetings' 
                    ? 'bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 text-white shadow-lg shadow-indigo-950/40' 
                    : 'bg-white/60 border border-indigo-200/70 text-indigo-900 hover:bg-white/75'
                }`}
              >
                <CalendarIcon className="h-4 w-4" />
                Meetings
              </button>
              
              <button 
                onClick={() => setActiveTab('suggestions')}
                className={`flex-1 min-w-[120px] inline-flex items-center justify-center gap-x-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold shadow-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  activeTab === 'suggestions' 
                    ? 'bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 text-white shadow-lg shadow-indigo-950/40' 
                    : 'bg-white/60 border border-indigo-200/70 text-indigo-900 hover:bg-white/75'
                }`}
              >
                <FlagIcon className="h-4 w-4" />
                AI Suggestions ({aiSuggestions.length})
              </button>
             
              </div>
            </div>

            {activeTab === 'meetings' && (
              <>
                <div className="mt-3 mb-3 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-2">
                  <div className="flex flex-wrap gap-2">
                    {user.access !== 'staff' && (
                  <button onClick={() => Navigate("/createmeeting")} className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-x-1.5 rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-2.5 py-2 text-xs font-semibold text-white border border-white/20 shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                    <CalendarIcon className="h-4 w-4" />
                    Create Meeting
                  </button>
                  )}
                  {!isStaffUser && (
                    <button onClick={() => setTravelComponent(true)} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-x-1.5 rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 px-2.5 py-2 text-xs font-semibold text-white border border-white/20 shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      <TruckIcon className="h-4 w-4" />
                      Travel
                    </button>
                  )}
                  {user.googleCalendarPermission ? (
                    <button disabled className="flex-1 min-w-[170px] inline-flex items-center justify-center gap-x-1.5 rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 px-2.5 py-2 text-xs font-semibold text-white border border-white/20 shadow-lg shadow-emerald-900/35">
                      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                      Google Calendar Synced
                    </button>
                  ) : (
                    <button
                      onClick={connectGoogleCalendar}
                      className="flex-1 min-w-[170px] inline-flex items-center justify-center gap-x-1.5 rounded-xl bg-white/80 px-2.5 py-2 text-xs font-semibold text-indigo-900 border border-indigo-200/70 shadow-sm hover:bg-white"
                    >
                      <img className="h-4 w-4" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
                      Sync Google Calendar
                    </button>
                  )}
                  </div>
                </div>
            {
              travelComponent && (
                <div className="relative my-4 p-4 rounded-xl bg-white shadow-lg border border-gray-200">
                  <button
                    onClick={() => setTravelComponent(false)}
                    className="absolute top-2 right-2 rounded-full bg-red-500 text-white px-2 py-1 hover:bg-red-700"
                  >
                    <XCircleIcon className="h-5 w-5 inline-block" aria-hidden="true" />
                    <span className="sr-only">Close</span>
                  </button>
                  <div className="text-sm text-gray-600 mt-4">
                    <Travel />
                  </div>
                </div>
              )
            }

            {/* Meetings List */}
            {meetings.length > 0 ? (
              sortedMeetings.map((meeting, idx) => {
                let meetingBackgroundColor = "bg-violet-100";
                let statusText = "Not Started";
                let showCancelButton = true;

                if (meeting.Start === 1 && meeting.Finished === 1) {
                  meetingBackgroundColor = "bg-purple-100";
                  statusText = "Meeting Completed";
                  showCancelButton = false;
                } else if (meeting.Start === 1 && (meeting.Finished === 0 || meeting.Finished === null)) {
                  meetingBackgroundColor = "bg-green-100";
                  statusText = "In Progress";
                }

                // Google Meet Styling Override
                if (meeting.Location === 'Google Meet') {
                  meetingBackgroundColor = "bg-[#e8f0fe] border-[#1a73e8]"; // Google Blue-ish tint
                }

                return (
                  <li
                    key={idx}
                    className={`relative mt-5 rounded-lg border ${meetingBackgroundColor} p-3 shadow-md sm:p-4`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex items-center gap-2">
                          {meeting.Location === 'Google Meet' && (
                            <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Google Meet" className="h-6 w-6 shrink-0" />
                          )}
                          <h3 className={`break-words text-base font-bold sm:text-lg ${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'}`}>{meeting.Name}</h3>
                        </div>
                        <span className="inline-flex items-center rounded-lg border border-indigo-200 bg-white/70 px-2 py-1 text-xs font-semibold text-indigo-800">
                          {meeting.Type || 'Meeting'}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex min-w-0 items-center space-x-2">
                          <ClockIcon className={`h-5 w-5 shrink-0 ${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'}`} aria-hidden="true" />
                          <span className={`${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'} break-words text-xs font-semibold sm:text-sm`}>{meeting.Time} - {meeting.FinishTime}</span>
                        </div>
                        <div className="flex min-w-0 items-center space-x-2">
                          <MapPinIcon className="h-5 w-5 shrink-0 text-black" aria-hidden="true" />
                          <span className={`${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'} break-words text-xs font-semibold sm:text-sm`}>{meeting.Location}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 rounded-lg border border-indigo-200/70 bg-white/70 p-2">
                      <div className="flex items-start space-x-2">
                        <UserCircleIcon className="h-5 w-5 shrink-0 text-black" aria-hidden="true" />
                        <div className="min-w-0">
                          <span className="block text-gray-600 font-medium">Attendees:</span>
                          <p className={`${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'} break-words whitespace-normal text-[11px] font-semibold leading-snug sm:text-xs`}>{meeting.Attendees}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2">
                      <span className="block text-gray-600 font-medium">Notes:</span>
                      <p className={`${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'} break-words whitespace-pre-wrap`}>{meeting.Notes}</p>
                    </div>
                    <div className="mt-2">
                      <span className="block text-gray-600 font-medium">AI Summary:</span>
                      <p className={`${meeting.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-blue-700'}`}>
                        {meeting.AISummary ? meeting.AISummary : "No AI Summary"}
                      </p>
                     
                    </div>
                    <div className="mt-2 text-center text-gray-600">
                      <span className={`block ${meeting.Start === 1 ? "font-bold text-black animate-pulse" : "font-bold text-black"} text-sm`}>
                        {statusText}
                      </span>
                    </div>

                    {showCancelButton && meeting.Interview === null && meeting.Location !== 'Google Meet' && (
                      <button onClick={() => {
                        const message = meeting;
                        if (meeting.Type === "InsightDocs") {
                          window.open('https://docs.insightos.co.uk', '_blank');
                        } else {
                          Navigate('/meeting', { state: { meeting: message } });
                        }
                      }}
                        className="rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:px-3.5 sm:py-2.5 sm:text-sm"
                      >
                        Start Meeting
                      </button>
                    )}


                    {/* Google Meet Join Button */}
                    {showCancelButton && meeting.Location === 'Google Meet' && meeting.GoogleMeetURL && (
                      <button
                        onClick={async () => {
                          // 1. Call backend to mark meeting as started
                          try {
                            await fetch(`https://api.insightos.co.uk/api/googlemeetstart/${meeting.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                            });
                            // 2. Open Google Meet URL in new tab
                            window.open(meeting.GoogleMeetURL, '_blank');
                            
                            // 3. Navigate to /meeting or open docs URL
                            if (meeting.Type === "InsightDocs") {
                              window.open('https://app.insightos.co.uk/support', '_blank');
                            } else {
                              Navigate('/meeting', { state: { meeting: meeting } });
                            }
                            
                          } catch (err) {
                            console.error('Failed to start Google Meet:', err);
                          }
                        }}
                        className="rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/20 px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 flex items-center gap-2 sm:px-3.5 sm:py-2.5 sm:text-sm"
                      >
                        <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Meet" className="h-4 w-4 sm:h-5 sm:w-5 bg-white rounded-full p-0.5" />
                        Join with Google Meet
                      </button>
                    )}

                    {showCancelButton && meeting.Location === 'Google Meet' && !meeting.GoogleMeetURL && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-xl border border-rose-200/70 bg-rose-50/80 px-3 py-2 text-xs font-semibold text-rose-700">
                          Google Meet link missing. Reconnect Google Calendar.
                        </span>
                        <button
                          onClick={connectGoogleCalendar}
                          className="inline-flex items-center gap-x-1.5 rounded-xl border border-indigo-200/70 bg-white/80 px-3 py-2 text-xs font-semibold text-indigo-900 shadow-sm hover:bg-white"
                        >
                          <img className="h-4 w-4" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
                          Reconnect Google Calendar
                        </button>
                        <button
                          onClick={() => Navigate('/meeting', { state: { meeting } })}
                          className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 px-3 py-2 text-xs font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95"
                        >
                          Open Meeting Workspace
                        </button>
                      </div>
                    )}

                    {showCancelButton && meeting.Interview === 1 && (
                      <button onClick={() => {
                        const message = meeting;
                        Navigate('/interview', { state: { meeting: message } });
                      }}
                        className="rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:px-3.5 sm:py-2.5 sm:text-sm"
                      >
                        Start Interview
                      </button>
                    )}
                    <div className="mt-4 flex justify-end space-x-3">
                      {showCancelButton && (
                        <button
                          onClick={() => handleDelete(meeting.id)}
                          type="button"
                          className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/20 px-2 py-1 text-xs font-semibold text-white shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all sm:px-2.5 sm:py-1.5 sm:text-sm"
                        >
                          Cancel Meeting
                          <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                        </button>
                      )}
                      {meeting.Start === 1 && meeting.Finished === 1 && (
                        <button
                          onClick={() => setReport(meeting)}
                          className="inline-flex items-center animate-pulse gap-x-1.5 rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/20 px-2 py-1 text-xs font-semibold text-white shadow-lg shadow-emerald-900/35 hover:from-emerald-500/95 hover:to-emerald-400/95 transition-all sm:px-2.5 sm:py-1.5 sm:text-sm"
                        >
                          Meeting Report
                          <DocumentIcon className="h-6 w-6 sm:h-10 sm:w-10" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })
            ) : (
              <div className="rounded-xl border border-indigo-200/60 bg-white/70 p-4 text-center text-indigo-800">
                No Meetings for this day.
              </div>
            )}
              </>
            )}

            {/* AI Suggestions Tab */}
            {activeTab === 'suggestions' && (
              <div className="mt-4">
                {sortedAISuggestions.length > 0 ? (
                  <div>
                    {sortedAISuggestions.map((suggestion, idx) => {
                      // Determine background color based on MeetingType
                      let suggestionBackgroundColor = "bg-blue-50"; // default
                      if (suggestion.MeetingType === "ToDo") {
                        suggestionBackgroundColor = "bg-purple-50";
                      } else if (suggestion.MeetingType === "Training") {
                        suggestionBackgroundColor = "bg-indigo-50";
                      } else if (suggestion.MeetingType === "Meeting") {
                        suggestionBackgroundColor = "bg-emerald-50";
                      }

                      return (
                        <li
                          key={idx}
                          className={`relative mt-5 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 p-4 shadow-[0_12px_30px_rgba(99,102,241,0.18)] backdrop-blur-xl`}
                        >
                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                              <h3 className="font-bold text-lg text-indigo-700">{suggestion.Title}</h3>
                            </div>
                            <div className="col-span-1 text-right">
                              <span className="block text-gray-600 font-medium mb-2">Type: {suggestion.MeetingType}</span>
                              {suggestion.Approved === null ? (
                                <button
                                  onClick={() => handleApproveAISuggestion(suggestion.id)}
                                  data-component="meeting-ai-suggestions"
                                  data-time-saved="3"
                                  data-feature="approve_ai_suggestion_post"
                                  className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/20 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-900/35 hover:from-emerald-500/95 hover:to-emerald-400/95 transition-all"
                                >
                                  <FlagIcon className="h-4 w-4" aria-hidden="true" />
                                  Approve
                                </button>
                              ) : (
                                <span className="inline-flex items-center gap-x-1.5 rounded-xl border border-indigo-200/70 bg-white/70 px-3 py-2 text-sm font-semibold text-indigo-800">
                                  Approved
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                              <span className="text-indigo-700 font-semibold">
                                {suggestion.TimeFrom} - {suggestion.TimeTo}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3">
                            <span className="block text-gray-600 font-medium">Content:</span>
                            <p className="text-gray-800 mt-1">{suggestion.Content}</p>
                          </div>
                          <div className="mt-3">
                            <span className="block text-gray-600 font-medium">Meeting Attendees:</span>
                            <p className="text-gray-800 mt-1">{suggestion.MeetingAttendees}</p>
                          </div>
                          <div className="mt-3">
                            <span className="block text-gray-600 font-medium">Training Doc Attachment</span>
                            <p className="text-gray-800 mt-1">{suggestion.TrainingDocName}</p>
                          </div>

                          {suggestion.Approved !== null && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">
                                Approved on: {new Date(suggestion.Approved).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-xl border border-indigo-200/60 bg-white/70 p-4 text-center text-indigo-800">
                    No AI Suggestions available.
                  </div>
                )}
              </div>
            )}
          </ol>
        </div>
      )}
    </div>
  );
}
